import { z } from 'zod';
import { geoFilterShape } from './geography.js';
import { haversineMeters } from './geo.js';
import { dateRangeQuerySchema } from './pagination.js';
/**
 * Time and distance between consecutive visits of one agent on one day (D85).
 *
 * The distance is the straight line between the two branches' verified
 * coordinates (haversine). It is NOT the distance travelled: streets, traffic
 * and detours are invisible to it, and no routing service is used. Every place
 * that shows it says so, with the labels below.
 */
export const STRAIGHT_LINE_LABEL = {
    ar: 'المسافة بخط مستقيم',
    en: 'Straight-line distance',
    noteAr: 'المسافة بخط مستقيم بين الفرعين (مش مسافة الطريق الفعلية).',
    noteEn: 'Straight line between the two branches (not the road distance travelled).',
};
/** Why a gap or a distance could not be computed, or needs a second look. */
export const GAP_FLAGS = [
    /** The previous visit has no usable end time (missing, or before its own start). */
    'NO_END_TIME',
    /** The next visit started before the previous one ended. */
    'OVERLAP',
    /** A visit's end time is earlier than its start time (device clock). */
    'INVERTED_TIMES',
    /** One of the two branches has no verified coordinates (or was not in the list): distance unknown, not zero. */
    'NO_COORDINATES',
];
const time = (value) => (value instanceof Date ? value : new Date(value)).getTime();
const round1 = (value) => Math.round(value * 10) / 10;
/**
 * Orders the day's visits by check-in time (ties by id, so the result never
 * depends on submission order) and computes one gap per consecutive pair. The
 * first visit of the day has no gap: it has no row here, and screens label it
 * as the first visit.
 */
export function computeVisitGaps(visits) {
    // Ties break on code-unit id order (not localeCompare): locale/ICU-independent,
    // so every runtime — and the Dart port — orders identically.
    const ordered = [...visits].sort((a, b) => time(a.startedAt) - time(b.startedAt) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    const gaps = [];
    for (let i = 1; i < ordered.length; i++) {
        const prev = ordered[i - 1];
        const next = ordered[i];
        const flags = [];
        const prevStart = time(prev.startedAt);
        const prevEnd = prev.endedAt === null ? null : time(prev.endedAt);
        let gapMinutes = null;
        let overlapMinutes = null;
        if (prevEnd === null || Number.isNaN(prevEnd)) {
            flags.push('NO_END_TIME');
        }
        else if (prevEnd < prevStart) {
            flags.push('INVERTED_TIMES', 'NO_END_TIME');
        }
        else {
            const raw = (time(next.startedAt) - prevEnd) / 60000;
            if (raw < 0) {
                flags.push('OVERLAP');
                overlapMinutes = round1(-raw);
            }
            else {
                gapMinutes = round1(raw);
            }
        }
        let distanceMeters = null;
        if (prev.branchPoint && next.branchPoint)
            distanceMeters = haversineMeters(prev.branchPoint, next.branchPoint);
        else
            flags.push('NO_COORDINATES');
        gaps.push({ fromVisitId: prev.id, toVisitId: next.id, gapMinutes, overlapMinutes, distanceMeters, flags });
    }
    return gaps;
}
export function gapTotals(visitCount, gaps) {
    const known = gaps.filter((g) => g.gapMinutes !== null);
    const located = gaps.filter((g) => g.distanceMeters !== null);
    return {
        visits: visitCount,
        pairs: gaps.length,
        totalGapMinutes: round1(known.reduce((sum, g) => sum + g.gapMinutes, 0)),
        knownGaps: known.length,
        unknownGaps: gaps.length - known.length,
        totalDistanceMeters: located.reduce((sum, g) => sum + g.distanceMeters, 0),
        knownDistances: located.length,
        unknownDistances: gaps.length - located.length,
    };
}
/** Averages over known values only; null when nothing is known (never a misleading 0). */
export function gapAverages(totals) {
    return {
        avgGapMinutes: totals.knownGaps > 0 ? round1(totals.totalGapMinutes / totals.knownGaps) : null,
        avgDistanceMeters: totals.knownDistances > 0 ? Math.round(totals.totalDistanceMeters / totals.knownDistances) : null,
    };
}
// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------
export const travelQuerySchema = z
    .object({
    agentId: z.uuid().optional(),
    ...geoFilterShape,
})
    .and(dateRangeQuerySchema);
export const travelDayQuerySchema = z.object({ agentId: z.uuid(), date: z.iso.date() });
const person = z.object({ id: z.string(), fullName: z.string() });
export const travelVisitRowSchema = z.object({
    visitId: z.string(),
    submissionId: z.string(),
    startedAt: z.string(),
    endedAt: z.string().nullable(),
    branchName: z.string().nullable(),
    brandName: z.string().nullable(),
    areaName: z.string().nullable(),
    /** False when the branch has no verified coordinates (its distances are unknown). */
    branchLocated: z.boolean(),
    /** Null for the first visit of the day. */
    fromPrevious: z
        .object({
        gapMinutes: z.number().nullable(),
        overlapMinutes: z.number().nullable(),
        distanceMeters: z.number().nullable(),
        flags: z.array(z.enum(GAP_FLAGS)),
    })
        .nullable(),
});
const totalsSchema = z.object({
    visits: z.number().int(),
    pairs: z.number().int(),
    totalGapMinutes: z.number(),
    knownGaps: z.number().int(),
    unknownGaps: z.number().int(),
    totalDistanceMeters: z.number(),
    knownDistances: z.number().int(),
    unknownDistances: z.number().int(),
});
export const travelDayResponseSchema = z.object({
    date: z.string(),
    agent: person,
    visits: z.array(travelVisitRowSchema),
    totals: totalsSchema,
});
export const travelSummaryResponseSchema = z.object({
    /** One row per agent per day. */
    days: z.array(z.object({ date: z.string(), agent: person }).extend(totalsSchema.shape)),
    /** Per agent over the range: averages over known values only. */
    agents: z.array(z.object({
        agent: person,
        days: z.number().int(),
        totals: totalsSchema,
        avgGapMinutes: z.number().nullable(),
        avgDistanceMeters: z.number().nullable(),
        avgVisitsPerDay: z.number().nullable(),
    })),
});
//# sourceMappingURL=travel.js.map